// Closed, typed source-class label for the Ask Helix evidence egress.
//
// Governed evidence artifacts carry a free-form `sourceClass` string (for
// example `modeled`, `connector-derived`, `public-filing`, `industry-reference`).
// The public answer surface must be able to disclose *how* an artifact was
// sourced — most importantly that a value came from `modeled`, pre-contract
// evidence — without ever leaking free-text provenance strings through egress.
//
// This module projects the open `sourceClass` vocabulary onto a fixed, seven-
// member label enum. The enum is a display projection only: it never changes
// which evidence is admitted, verified, or gated. The default projection is
// `approved_source`, so approved governed evidence renders exactly as before.
import { sourceClassPackagePosture } from './contracts/sourceClasses';

export const SOURCE_CLASS_LABEL_VALUES = [
  'approved_source',
  'planning_view',
  'source_pending',
  'modeled',
  'industry_reference',
  'asker_stated',
  'gated',
] as const;

export type SourceClassLabel = (typeof SOURCE_CLASS_LABEL_VALUES)[number];

const SOURCE_CLASS_LABEL_SET: ReadonlySet<string> = new Set(SOURCE_CLASS_LABEL_VALUES);

export type PublicDecisionEvidenceKind =
  | 'approved_source_evidence'
  | 'decision_agent_proof';

// Human-facing short label for each enum member. Fixed vocabulary; never free
// text derived from the artifact.
const SOURCE_CLASS_LABEL_DISPLAY: Readonly<Record<SourceClassLabel, string>> = Object.freeze({
  approved_source: 'Approved source',
  planning_view: 'Planning view',
  source_pending: 'Source confirmation needed',
  modeled: 'Modeled',
  industry_reference: 'Industry reference',
  asker_stated: 'Asker-stated',
  gated: 'Gated',
});

export function isSourceClassLabel(value: unknown): value is SourceClassLabel {
  return typeof value === 'string' && SOURCE_CLASS_LABEL_SET.has(value);
}

export function sourceClassLabelDisplay(label: SourceClassLabel): string {
  return SOURCE_CLASS_LABEL_DISPLAY[label];
}

// `approved_source` is the default surface treatment (no distinct disclosure
// badge). Every other label is a mandatory visible disclosure: planning_view is
// a calculated planning posture rather than a committed figure, source_pending
// means the value remains visible while its evidence identity is repaired,
// modeled evidence is pre-contract, industry_reference is a benchmark rather
// than the tenant's own data, asker_stated originated from the asker, and gated
// evidence sits behind an opt-in gate.
export function sourceClassLabelRequiresDisclosure(label: SourceClassLabel): boolean {
  return label !== 'approved_source';
}

function normalizeSourceClass(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim().toLowerCase()
    : null;
}

// Deterministic projection from the raw artifact `sourceClass` to the closed
// label enum. Absent stays `approved_source` (unchanged governed modules);
// an unrecognized class is never inferred approved (#6950) — it routes
// through the canonical posture contract instead of a blind default.
export function mapSourceClassToLabel(
  sourceClass: string | null | undefined,
): SourceClassLabel {
  const normalized = normalizeSourceClass(sourceClass);
  if (!normalized) {
    return 'approved_source';
  }

  if (
    normalized === 'planning-derived' ||
    normalized === 'planning_view' ||
    normalized === 'planning-view'
  ) {
    return 'planning_view';
  }

  if (normalized === 'source-pending' || normalized === 'source_pending') {
    return 'source_pending';
  }

  if (normalized === 'modeled') {
    return 'modeled';
  }

  if (normalized === 'scenario') {
    return 'modeled';
  }

  if (
    normalized === 'industry-reference' ||
    normalized === 'industry_reference' ||
    normalized.startsWith('industry-reference') ||
    normalized.startsWith('industry_reference')
  ) {
    return 'industry_reference';
  }

  if (
    normalized === 'asker-stated' ||
    normalized === 'asker_stated' ||
    normalized.startsWith('asker-stated') ||
    normalized.startsWith('asker_stated')
  ) {
    return 'asker_stated';
  }

  // Evidence behind an opt-in gate (for example a workforce legal gate).
  if (normalized === 'gated' || /(^|[-_])gate(d)?([-_]|$)/.test(normalized)) {
    return 'gated';
  }

  // #6950 (scope correction 5558160192): ONLY the canonical modeled posture
  // (synthetic-demo, extrapolated) is repaired here — it never claims
  // approved_source. Every other class, including any unrecognized string,
  // keeps the existing default (unchanged governed modules).
  if (sourceClassPackagePosture(normalized) === 'modeled') {
    return 'modeled';
  }
  return 'approved_source';
}

export function publicDecisionEvidenceKind(
  sourceClass: string | null | undefined,
): PublicDecisionEvidenceKind {
  return mapSourceClassToLabel(sourceClass) === 'approved_source'
    ? 'approved_source_evidence'
    : 'decision_agent_proof';
}
